import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import {
	selectPaymentEligibility,
	selectPolicyHolderEmail,
	selectRateData,
} from '@acorn-portal/shared/src/store/cancellation/cancellation.selectors';
import { CheckCircleOutlineRounded } from '@mui/icons-material';
import { Alert, Link, styled, Typography } from '@mui/material';
import Heading from './components/heading';
import RefundInformation from './components/refund-information';
import Section from './components/section';
import Subheading from './components/sub-heading';
import WaivedBalanceNotice from './components/waived-balance-notice';
import ContentWrapper from './components/wrapper/content-wrapper';
import PageWrapper from './components/wrapper/page-wrapper';
import { FeeType } from '@acorn-portal/shared/src/services/cancellation-api';
import { useCancellationDateDisplay } from './hooks/use-cancellation-date-display';
import { useEffect } from 'react';
import useTrack from '@acorn-portal/shared/src/hooks/useTrack/useTrack';

const HighlightSection = styled(Section)(({ theme }) => ({
	backgroundColor: theme.palette.primary.light,
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	padding: '16px 24px',
	borderRadius: '8px',
}));

const WarningLink = styled(Link)({
	color: 'inherit',
	textDecoration: 'underline',
	'&:hover': { textDecoration: 'underline' },
});

const CancellationSuccess = () => {
	const rateData = useAppSelector(selectRateData);
	const policyHolderEmail = useAppSelector(selectPolicyHolderEmail);
	const { eligibleForRefund, eligibleForClearBalance } = useAppSelector(selectPaymentEligibility);
	const { showImmediateCancellationMessage, requested, actual } = useCancellationDateDisplay();
	const displayEmail = policyHolderEmail || 'your registered email';

	const { track } = useTrack();

	useEffect(() => {
		if (!track) return;
		track('cancellation_step_4_started', { name: 'cancellation_success_page', step_number: 4 });
	}, []);

	const timedCancellationDateMessage = actual ? (
		<>
			Your policy is due to be cancelled at <strong>{actual.time}</strong> on the <strong>{actual.date}</strong>.
		</>
	) : (
		<>
			Your policy will end at <strong>{requested.time}</strong> on the <strong>{requested.date}</strong>.
		</>
	);

	return (
		<PageWrapper>
			<ContentWrapper>
				<Heading>We&apos;re sorry to see you go!</Heading>

				<HighlightSection>
					<CheckCircleOutlineRounded
						sx={{ height: 64, width: 64, color: 'primary.dark', marginBottom: '24px', alignContent: 'center' }}
					/>
					<Subheading>Cancellation confirmed</Subheading>
					<Typography textAlign='center'>
						Once cancelled, we will send a confirmation to <strong>{displayEmail}</strong>. If eligible, your proof of
						No Claims Discount will be sent within <strong>72 hrs</strong> of your policy being cancelled.
					</Typography>
				</HighlightSection>

				<Section>
					<Subheading>Cancellation Date</Subheading>
					<Typography>
						{showImmediateCancellationMessage
							? 'Following confirmation of your cancellation, your policy will be cancelled within 15 minutes'
							: timedCancellationDateMessage}
					</Typography>
				</Section>

				{eligibleForRefund && (
					<Section>
						<Subheading>Your refund</Subheading>
						<RefundInformation
							refundAmount={rateData?.finalAmount ?? 0}
							cancellationFee={rateData?.cancellationFee ?? 0}
							totalRefundAmount={rateData?.initialAmount ?? 0}
							insurancePremiumTax={rateData?.insurancePremiumTax ?? 0}
							feeType={rateData?.feeType ?? FeeType.CancellationFee}
						/>
					</Section>
				)}

				{eligibleForClearBalance && <WaivedBalanceNotice />}

				<Alert severity='info' variant='outlined' sx={{ borderRadius: '4px' }}>
					It is an offence under the Road Traffic Act 1988 to leave, drive or otherwise use your vehicle on a public
					highway without valid insurance cover being in force, unless you&apos;ve registered your vehicle off the road
					(SORN) with the DVLA by visiting{' '}
					<WarningLink href={'https://www.gov.uk'} target='_blank' rel='noopener noreferrer'>
						www.gov.uk
					</WarningLink>
				</Alert>
			</ContentWrapper>
		</PageWrapper>
	);
};

export default CancellationSuccess;
